import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { debounceTime, distinctUntilChanged, map, merge, Observable, of, Subject, switchMap } from 'rxjs';
import { DropdownComponent, ItemSelectedEvent } from '../../dropdown/dropdown.component';
import { ClientOrdersState, ClientOrdersStore } from './client-orders.store';
import { ClientOrderStatus } from './models/client-order-list-item.dto';
import { CLIENT_ORDER_STATUS_LABELS } from './models/client-order-row.view-model';
import { OrderFaceComponent } from '../../order-face/order-face.component';

interface StatusFilterOption {
  status: ClientOrderStatus | undefined;
  label: string;
}

const STATUS_FILTER_OPTIONS: StatusFilterOption[] = [
  { status: undefined, label: 'Wszystkie' },
  { status: ClientOrderStatus.new, label: CLIENT_ORDER_STATUS_LABELS[ClientOrderStatus.new] },
  { status: ClientOrderStatus.confirmed, label: CLIENT_ORDER_STATUS_LABELS[ClientOrderStatus.confirmed] },
  { status: ClientOrderStatus.inProduction, label: CLIENT_ORDER_STATUS_LABELS[ClientOrderStatus.inProduction] },
  { status: ClientOrderStatus.partiallyShipped, label: CLIENT_ORDER_STATUS_LABELS[ClientOrderStatus.partiallyShipped] },
  { status: ClientOrderStatus.shipped, label: CLIENT_ORDER_STATUS_LABELS[ClientOrderStatus.shipped] },
];

/**
 * The master view of the order book: every zamówienie with its derived state and how
 * many of its positions are covered by zlecenia and by wysyłki — a dense list to scan
 * and filter, not a graph (0016). The per-order picture is the detail page.
 */
@Component({
  selector: 'client-orders-page',
  templateUrl: './client-orders.page.html',
  styleUrl: './client-orders.page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, RouterLink, DropdownComponent, OrderFaceComponent]
})
export class ClientOrdersPage implements OnInit, OnDestroy {
  readonly state$: Observable<ClientOrdersState>;
  readonly statusLabels = CLIENT_ORDER_STATUS_LABELS;

  // the list's shadow while it loads (0128): five rows at the row's height
  readonly shadowRows = [0, 1, 2, 3, 4];

  private readonly searchTerm$ = new Subject<string>();
  private readonly statusFilter$ = new Subject<StatusFilterOption['status']>();

  constructor(
    private store: ClientOrdersStore,
    private destroyRef: DestroyRef
  ) {
    this.state$ = this.store.state$;
  }

  ngOnInit(): void {
    this.store.init().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    // One stream for both criteria: switchMap drops the in-flight request
    // when a newer term or filter arrives, so a stale response can never
    // overwrite fresher results (bom-list precedent)
    merge(
      this.searchTerm$.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        map(term => () => this.store.search(term))
      ),
      this.statusFilter$.pipe(
        map(status => () => this.store.filterByStatus(status))
      )
    )
      .pipe(
        switchMap(request => request()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  onSearchInput(term: string): void {
    this.searchTerm$.next(term);
  }

  searchStatusOptions = (searchTerm: string) => of(
    STATUS_FILTER_OPTIONS.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  formatStatusOption = (option: StatusFilterOption) => option.label;

  onStatusSelected(event: ItemSelectedEvent<StatusFilterOption>): void {
    // Enter on a raw search term selects nothing — the filter only has
    // its fixed options
    if (!event.item) {
      return;
    }

    this.statusFilter$.next(event.item.status);
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }
}
